import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { base44 } from "@/api/base44Client";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";

const AnimatedElement = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => setIsVisible(true), delay);
        observer.unobserve(el);
      }
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return (
    <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'} ${className}`}>
      {children}
    </div>
  );
};

function AddOnCard({ addon, index }) {
  return (
    <AnimatedElement delay={index * 100}>
      <motion.div
        whileHover={{ y: -8, scale: 1.02 }}
        transition={{ duration: 0.3 }}
        className="group bg-card border border-border/50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
      >
        <div className="p-6 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="font-bold text-xl text-foreground group-hover:text-primary transition-colors">
              {addon.name}
            </h3>
          </div>
          {addon.description && (
            <p className="text-muted-foreground text-sm leading-relaxed">
              {addon.description}
            </p>
          )}
          <div className="flex items-center justify-between mt-2">
            <span className="text-2xl font-bold text-primary">+${addon.price}</span>
            <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">Add-On</span>
          </div>
        </div>
      </motion.div>
    </AnimatedElement>
  );
}

export default function AddOnsPage() {
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    base44.entities.AddOn.filter({ active_status: true })
      .then(setAddOns)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <section className="relative bg-gradient-to-br from-green-400/10 via-background to-emerald-300/10 py-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <AnimatedElement>
            <h1 className="font-extrabold text-4xl sm:text-5xl md:text-6xl text-foreground mb-4">
              Add-Ons & Extras
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
              Generators, lighting, and extras to complete your perfect event setup.
            </p>
          </AnimatedElement>
        </div>
      </section>

      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          {addOns.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-muted-foreground">No add-ons available at the moment.</p>
              <Link to="/browse-rentals" className="mt-4 inline-block text-primary hover:underline">
                Browse rentals →
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {addOns.map((addon, index) => (
                <AddOnCard key={addon.id} addon={addon} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}